import React, { useState } from "react"

import {
	CircularProgress,
	Grid,
	Tab,
	Tabs,
	Typography
} from "@material-ui/core"

import useStyles from "@/pages/Admin/ClientCatalog/ImportContacts/styles"

import { Breadcrumb, Notification } from "@/components"
import useValidation, { ErrorType } from "@/hooks/useValidation"
import useDidMount from "@/hooks/useDidMount"

import ApiService from "@/services/Api"
import useSocket from "@/hooks/useSocket"
import ErrorHandler from "@/services/ErrorHandler"
import ImportContactsHistory from "@/pages/Admin/ClientCatalog/ImportContacts/ImportContactsHistory"
import ImportContactsReport from "@/pages/Admin/ClientCatalog/ImportContacts/ImportContactsReport"
import ImportContactsByText from "@/pages/Admin/ClientCatalog/ImportContacts/ImportContactsByText"
import ImportContactsByCSV from "@/pages/Admin/ClientCatalog/ImportContacts/ImportContactByCSV"
import ImportContactsDetection from "@/pages/Admin/ClientCatalog/ImportContacts/ImportContactsDetection"
import AdvancedDeploymentDialog from "@/pages/Admin/ClientCatalog/ImportContacts/AdvancedDeploymentDialog"
import {
	ContactsBeingProcessedScreenProps,
	ImportContactsFormValuesType,
	ImportContactsReportType,
	ImportTab,
	ImportContactsHistoryType,
	DetectedColumnsType,
	ColumnMappingType,
	FileMetadataType,
	ExtraColumnMappingDecisionType
} from "@/protocols/clientCatalogImportProtocol"
import { CustomField } from "@/protocols/customField"

type ImportStep = "upload" | "detection" | "processing"

const EMPTY_IMPORT_CONTACTS_FORM_VALUES: ImportContactsFormValuesType = {
	duplicate_contacts_action: "replace",
	contacts_file: undefined
}

const IMPORT_TABS: ImportTab[] = [
	{ title: "IMPORTAÇÃO POR ARQUIVO", type: "import-by-file", slug: "file" },
	{ title: "IMPORTAÇÃO POR TEXTO", type: "import-by-text", slug: "text" },
	{ title: "COM AJUDA DA LETALK", type: "import-with-help", slug: "help" }
]

const ImportContacts = () => {
	const [currentImportTabType, setCurrentImportTabType] = useState<ImportTab["type"]>("import-by-file")
	const classes = useStyles()

	const [loading, setLoading] = useState(false)
	const [openImportContactsReportDialog, setOpenImportContactsReportDialog] = useState(false)
	const [isContactsToImportBeingProcessed, setIsContactsToImportBeingProcessed] = useState(false)
	const [selectedImportContactsHistory, setSelectedImportContactsHistory] = useState<ImportContactsHistoryType>()
	const [totalContactsProcessed, setTotalContactsProcessed] = useState<number>(0)
	const [importContactsHistoryList, setImportContactsHistoryList] = useState<ImportContactsHistoryType[]>([])
	const [importContactsFormValues, setImportContactsFormValues] = useState<ImportContactsFormValuesType>({
		duplicate_contacts_action: "replace"
	})
	const [whatsappNumbersToImport, setWhatsappNumbersToImport] = useState<string>("")

	const [importStep, setImportStep] = useState<ImportStep>("upload")
	const [detectedColumns, setDetectedColumns] = useState<DetectedColumnsType | null>(null)
	const [pendingCsvFile, setPendingCsvFile] = useState<File | null>(null)
	const [pendingDuplicateAction, setPendingDuplicateAction] = useState<ImportContactsFormValuesType["duplicate_contacts_action"]>("replace")
	const [pendingFileMetadata, setPendingFileMetadata] = useState<FileMetadataType | null>(null)
	const [columnMappings, setColumnMappings] = useState<ColumnMappingType[]>([])
	const [extraColumnDecisions, setExtraColumnDecisions] = useState<Record<string, ExtraColumnMappingDecisionType>>({})
	const [availableCustomFields, setAvailableCustomFields] = useState<CustomField[]>([])

	const { validation, clearValidation, triggerValidation } = useValidation()
	const socket = useSocket()

	const ContactsBeingProcessedScreen = ({ totalContactsProcessed: count }: ContactsBeingProcessedScreenProps) => (
		<Grid container direction="column" justifyContent="center" alignItems="center" spacing={4}>
			<Grid item>
				<CircularProgress color="secondary" />
			</Grid>
			<Grid item>
				<Typography variant="h4">IMPORTANDO CONTATOS</Typography>
			</Grid>
			<Grid item>
				<Typography variant="body1" align="center">
					<b>{count}</b> contato{count > 1 && "s"} processados até o momento
				</Typography>
			</Grid>
			<Grid item>
				<Typography variant="body1" align="center">O arquivo está sendo processado.</Typography>
				<Typography variant="body1" align="center">
					Você pode fechar essa tela a qualquer momento que não irá afetar na importação dos contatos.
				</Typography>
			</Grid>
		</Grid>
	)

	const handleGetImportContactsHistory = async () => {
		setLoading(true)
		try {
			const response = await ApiService.get("/clients/contacts/imports-history")
			const parsedImportContactsHistoryList: ImportContactsHistoryType[] = response.data.map((importContactsReport: ImportContactsReportType) => {
				if (importContactsReport.status === "importing") {
					setIsContactsToImportBeingProcessed(true)
					setTotalContactsProcessed(importContactsReport.consolidated_data.total_contacts)
				}
				return {
					id: importContactsReport.id,
					instanceId: importContactsReport.instance_id,
					consolidatedData: {
						totalContacts: importContactsReport.consolidated_data.total_contacts,
						totalContactsReplaced: importContactsReport.consolidated_data.total_contacts_replaced,
						totalNewContacts: importContactsReport.consolidated_data.total_new_contacts
					},
					contactsNotImported: importContactsReport.contacts_not_imported,
					status: importContactsReport.status,
					createdAt: importContactsReport.created_at,
					importType: importContactsReport.import_type
				}
			})
			setImportContactsHistoryList(parsedImportContactsHistoryList)
		} catch {
			Notification.error({ message: "Houve um erro ao tentar obter o histórico de importação de contatos." })
		}
		setLoading(false)
	}

	const handleLoadAvailableCustomFields = async () => {
		try {
			const response = await ApiService.get("/custom-fields")
			const loadedCustomFields = response.data.customFields

			if (loadedCustomFields) {
				setAvailableCustomFields(loadedCustomFields)
			} else {
				setAvailableCustomFields([])
			}
		} catch {
			Notification.error({ message: "Não foi possível carregar os campos personalizados." })
		}
	}

	const dispatchImport = async (
		file: File,
		duplicateAction: ImportContactsFormValuesType["duplicate_contacts_action"],
		mappings?: ColumnMappingType[]
	) => {
		setTotalContactsProcessed(0)
		setIsContactsToImportBeingProcessed(true)
		setImportStep("processing")

		const formData = new FormData()
		formData.append("contactsFile", file)
		formData.append("duplicateContactsAction", duplicateAction)
		if (mappings) {
			formData.append("columnMappings", JSON.stringify(mappings))
		}

		await ApiService.post("/clients/contacts/import-from-file", formData, {
			headers: { "Content-Type": "multipart/form-data" }
		})
	}

	const handleImportContactsByFile = async () => {
		try {
			if (importContactsFormValues.contacts_file) {
				await dispatchImport(importContactsFormValues.contacts_file, importContactsFormValues.duplicate_contacts_action)
			}
		} catch (error) {
			setIsContactsToImportBeingProcessed(false)
			setImportStep("upload")
			triggerValidation(error as ErrorType)
			Notification.error({ message: "Houve um erro ao importar os contatos." })
		}
		setImportContactsFormValues(EMPTY_IMPORT_CONTACTS_FORM_VALUES)
	}

	const handleContactsImportByText = async () => {
		try {
			setTotalContactsProcessed(0)
			setIsContactsToImportBeingProcessed(true)
			await ApiService.post("/clients/contacts/import-from-text", {
				textWithPhones: whatsappNumbersToImport,
				origin: "text"
			})
			Notification.success({ message: "Contatos adicionados com sucesso!" })
		} catch (error) {
			Notification.error({ message: "Não foi possível adicionar os contatos!" })
			ErrorHandler.handle(error as Error)
		}
		setIsContactsToImportBeingProcessed(false)
		setWhatsappNumbersToImport("")
	}

	const handleContactsToImportFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target
		clearValidation(name)
		if (name === "contacts_file" && event.target.files) {
			setImportContactsFormValues({ ...importContactsFormValues, [name]: event.target.files[0] })
		} else {
			setImportContactsFormValues({ ...importContactsFormValues, [name]: value })
		}
	}

	const handleFileDrop = (file: File | null) => {
		clearValidation("contacts_file")
		const selectedFile = file || undefined
		setImportContactsFormValues((prev) => ({ ...prev, contacts_file: selectedFile }))
	}

	const handleDetectionComplete = async (
		result: DetectedColumnsType,
		file: File,
		duplicateAction: ImportContactsFormValuesType["duplicate_contacts_action"],
		fileMetadata: FileMetadataType
	) => {
		setPendingCsvFile(file)
		setPendingDuplicateAction(duplicateAction)
		setPendingFileMetadata(fileMetadata)

		const standardMappings: ColumnMappingType[] = result.standardColumns.map((detectedColumn) => ({
			csvHeader: detectedColumn.csvHeader,
			mappedTo: detectedColumn.mappedField
		}))
		setColumnMappings(standardMappings)

		if (result.extraColumns.length === 0) {
			try {
				await dispatchImport(file, duplicateAction, standardMappings)
			} catch (error) {
				setIsContactsToImportBeingProcessed(false)
				setImportStep("upload")
				triggerValidation(error as ErrorType)
				Notification.error({ message: "Houve um erro ao importar os contatos." })
			}
			return
		}

		await handleLoadAvailableCustomFields()
		setExtraColumnDecisions({})
		setDetectedColumns(result)
		setImportStep("detection")
	}

	const handlePromoteExtraColumn = (csvHeader: string) => {
		setExtraColumnDecisions((prev) => ({ ...prev, [csvHeader]: null }))
	}

	const handleDemoteExtraColumn = (csvHeader: string) => {
		setExtraColumnDecisions((prev) => {
			const remainingDecisions = { ...prev }
			delete remainingDecisions[csvHeader]
			return remainingDecisions
		})
	}

	const mapDecisionToColumnMapping = (csvHeader: string, decision: ExtraColumnMappingDecisionType): ColumnMappingType => {
		if (!decision) {
			return { csvHeader, mappedTo: "skip" }
		}

		if (decision.kind === "existing") {
			return { csvHeader, mappedTo: "custom_field", customFieldId: decision.customFieldId }
		}

		return { csvHeader, mappedTo: "custom_field", newFieldName: decision.fieldName }
	}

	const handleDetectionConfirm = async () => {
		if (!pendingCsvFile || !detectedColumns) {
			return
		}
		try {
			const extraColumnMappings = detectedColumns.extraColumns.map(
				(csvHeader) => mapDecisionToColumnMapping(csvHeader, extraColumnDecisions[csvHeader])
			)

			await dispatchImport(pendingCsvFile, pendingDuplicateAction, [...columnMappings, ...extraColumnMappings])
		} catch (error) {
			setIsContactsToImportBeingProcessed(false)
			setImportStep("detection")
			triggerValidation(error as ErrorType)
			Notification.error({ message: "Houve um erro ao importar os contatos." })
		}
	}

	const handleOnContactsImportStatusChanged = () => {
		socket.onContactsImportStatusChanged((contactsImport: ImportContactsHistoryType) => {
			if (contactsImport.status === "importing") {
				const totalContacts = contactsImport?.consolidatedData?.totalContacts

				if (totalContacts) {
					setTotalContactsProcessed(totalContacts)
				}
			} else {
				setSelectedImportContactsHistory(contactsImport)
				setIsContactsToImportBeingProcessed(false)
				setOpenImportContactsReportDialog(true)
				setImportStep("upload")
				setDetectedColumns(null)
				setPendingCsvFile(null)
				setPendingFileMetadata(null)
				setColumnMappings([])
				setExtraColumnDecisions({})
				setImportContactsFormValues(EMPTY_IMPORT_CONTACTS_FORM_VALUES)
				handleGetImportContactsHistory()
			}
		})
	}

	const breadCrumbPathsData = [
		{ name: "Contatos", pathname: "/admin/client-catalog" },
		{ name: "Importar Contatos", pathname: "/admin/client-catalog/import-contacts" }
	]

	useDidMount(() => {
		handleGetImportContactsHistory()
		handleOnContactsImportStatusChanged()
	})

	const csvUploadComponent = (
		<ImportContactsByCSV
			loading={loading}
			isContactsToImportBeingProcessed={isContactsToImportBeingProcessed}
			totalContactsProcessed={totalContactsProcessed}
			importContactsFormValues={importContactsFormValues}
			validation={validation}
			handleContactsToImportFileInputChange={handleContactsToImportFileInputChange}
			handleImportContactsByFile={handleImportContactsByFile}
			ContactsBeingProcessedScreen={ContactsBeingProcessedScreen}
			onDetectionComplete={handleDetectionComplete}
			onFileDrop={handleFileDrop}
		/>
	)

	const importStepComponentMap: Record<ImportStep, () => React.ReactNode> = {
		upload: () => csvUploadComponent,
		detection: () => detectedColumns
			? (
				<ImportContactsDetection
					detectedColumns={detectedColumns}
					fileName={pendingCsvFile ? pendingCsvFile.name : ""}
					fileMetadata={pendingFileMetadata}
					availableCustomFields={availableCustomFields}
					extraColumnDecisions={extraColumnDecisions}
					onPromoteExtraColumn={handlePromoteExtraColumn}
					onDemoteExtraColumn={handleDemoteExtraColumn}
					onExtraColumnDecisionChange={(csvHeader, decision) =>
						setExtraColumnDecisions((prev) => ({ ...prev, [csvHeader]: decision }))
					}
					onChangeFile={() => {
						setImportStep("upload")
						setImportContactsFormValues(EMPTY_IMPORT_CONTACTS_FORM_VALUES)
					}}
					onConfirm={handleDetectionConfirm}
					loading={loading}
				/>
			)
			: null,
		processing: () => (
			<ContactsBeingProcessedScreen totalContactsProcessed={totalContactsProcessed} />
		)
	}

	return (
		<Grid container spacing={4}>
			<Grid item xs={12}>
				<Breadcrumb data={breadCrumbPathsData} />
			</Grid>

			<Grid item xs={12}>
				<Grid container direction="column" spacing={2}>
					<Grid item>
						<Tabs
							color="primary"
							value={currentImportTabType}
							className={classes.tabs}
							onChange={(_, value) => {
								if (value === "import-with-help") {
									AdvancedDeploymentDialog.open()
									return
								}
								setCurrentImportTabType(value)
							}}
							classes={{ indicator: classes.tabIndicator }}
						>
							{IMPORT_TABS.map((tab) => (
								<Tab
									key={tab.slug}
									label={<span>{tab.title}</span>}
									value={tab.type}
									className={classes.tab}
								/>
							))}
						</Tabs>
					</Grid>

					<Grid item>
						{currentImportTabType === "import-by-file" && (
							importStepComponentMap[importStep]()
						)}
					</Grid>

					<Grid item>
						{currentImportTabType === "import-by-text" && (
							<ImportContactsByText
								loading={loading}
								isContactsToImportBeingProcessed={isContactsToImportBeingProcessed}
								totalContactsProcessed={totalContactsProcessed}
								whatsappNumbersToImport={whatsappNumbersToImport}
								handleChangeWhatsappNumbersToImport={setWhatsappNumbersToImport}
								handleContactsImportByText={handleContactsImportByText}
								ContactsBeingProcessedScreen={ContactsBeingProcessedScreen}
							/>
						)}
					</Grid>

					<Grid item xs={12}>
						<Grid container direction="column" spacing={1}>
							<Grid item xs={12}>
								<Typography variant="h4" color="textPrimary">
									HISTÓRICO DE IMPORTAÇÕES
								</Typography>
							</Grid>
							<Grid item xs={12}>
								<ImportContactsHistory
									importContactsHistoryList={importContactsHistoryList}
									setOpenImportContactsReportDialog={setOpenImportContactsReportDialog}
									setSelectedImportContactsHistory={setSelectedImportContactsHistory}
								/>
							</Grid>
						</Grid>
					</Grid>

					<ImportContactsReport
						openDialog={openImportContactsReportDialog}
						setOpenDialog={setOpenImportContactsReportDialog}
						importContactsHistory={selectedImportContactsHistory}
					/>
				</Grid>
			</Grid>
		</Grid>
	)
}

export default ImportContacts
